import { z } from 'zod';
import { pageInfoSchema } from './pagination';
import {
  CUSTOM_EMOJI_NAME_PATTERN,
  MESSAGING_LIMITS,
  PRODUCTIVITY_LIMITS,
  USER_GROUP_HANDLE_PATTERN,
} from '../enums';
import { richTextDocWireSchema } from '../richtext/schema';
import { isoDateTimeSchema, multiLineText, singleLineText, uuidSchema } from './common';
import { bookmarkDtoSchema } from './bookmarks';
import { messageDtoSchema } from './messaging';

/**
 * Channel extras contracts (PLAN §4 bookmarks, pins, forward, unfurls, user groups, custom emoji;
 * SPEC §4.2, §4.3). Owned by the `channel-extras` API module (apps/api/src/modules/channel-extras)
 * and the web extras stream (features/pins, bookmarks, unfurls, emoji-admin, user-groups).
 */

const emojiShortcode = z.string().regex(/^[a-z0-9_+-]{1,64}(::skin-tone-[2-6])?$/, 'Unknown emoji');
const httpUrl = z
  .string()
  .trim()
  .max(2_048)
  .regex(/^https?:\/\//i, 'Use an http(s) URL')
  .pipe(z.url());

// ================================================================ bookmarks bar

export const bookmarkParamsSchema = z.object({
  conversationId: uuidSchema,
  bookmarkId: uuidSchema,
});
export type BookmarkParams = z.infer<typeof bookmarkParamsSchema>;

export const bookmarkListResponseSchema = z.object({
  items: z.array(bookmarkDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});
export type BookmarkListResponse = z.infer<typeof bookmarkListResponseSchema>;

/**
 * POST /conversations/:conversationId/bookmarks — exactly one of `url`, `fileId`, `messageId`.
 * A file must be one the caller uploaded or one already attached to a message in this
 * conversation (`422 file_not_attachable`, D-111). At most 50 (`409 bookmark_limit`).
 */
export const createBookmarkRequestSchema = z
  .strictObject({
    title: singleLineText(1, PRODUCTIVITY_LIMITS.bookmarkTitleChars),
    url: httpUrl.optional(),
    fileId: uuidSchema.optional(),
    messageId: uuidSchema.optional(),
    emoji: emojiShortcode.nullable().optional(),
  })
  .refine(
    (b) => [b.url, b.fileId, b.messageId].filter((v) => v !== undefined).length === 1,
    'Send exactly one of url, fileId and messageId',
  );
export type CreateBookmarkRequest = z.infer<typeof createBookmarkRequestSchema>;

export const updateBookmarkRequestSchema = z.strictObject({
  title: singleLineText(1, PRODUCTIVITY_LIMITS.bookmarkTitleChars).optional(),
  url: httpUrl.optional(),
  emoji: emojiShortcode.nullable().optional(),
});
export type UpdateBookmarkRequest = z.infer<typeof updateBookmarkRequestSchema>;

/** PUT /conversations/:conversationId/bookmarks/order — every bookmark id in the new order. */
export const reorderBookmarksRequestSchema = z.strictObject({
  bookmarkIds: z.array(uuidSchema).min(1).max(PRODUCTIVITY_LIMITS.maxBookmarksPerConversation),
});
export type ReorderBookmarksRequest = z.infer<typeof reorderBookmarksRequestSchema>;

// ================================================================ pins

export const pinnedItemDtoSchema = z.object({
  message: messageDtoSchema,
  pinnedBy: uuidSchema,
  pinnedAt: isoDateTimeSchema,
});
export type PinnedItemDto = z.infer<typeof pinnedItemDtoSchema>;

/** GET /conversations/:conversationId/pins — newest pin first. */
export const pinListResponseSchema = z.object({
  items: z.array(pinnedItemDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});
export type PinListResponse = z.infer<typeof pinListResponseSchema>;

// ================================================================ forward (share)

/**
 * POST /messages/:messageId/forward — `Idempotency-Key` required; conv-read on the source,
 * conv-member on each target. The forwarded copy carries `forwardedFrom` and shares the source's
 * files (the only way someone else's file travels, D-111).
 */
export const forwardMessageRequestSchema = z.strictObject({
  conversationIds: z.array(uuidSchema).min(1).max(PRODUCTIVITY_LIMITS.maxForwardTargets),
  /** An optional comment in the message body format (D-045): one of `content` and `text`. */
  content: richTextDocWireSchema.optional(),
  text: z
    .string()
    .max(MESSAGING_LIMITS.maxPlainTextChars * 2)
    .optional(),
});
export type ForwardMessageRequest = z.infer<typeof forwardMessageRequestSchema>;

export const forwardMessageResponseSchema = z.object({ messages: z.array(messageDtoSchema) });
export type ForwardMessageResponse = z.infer<typeof forwardMessageResponseSchema>;

// ================================================================ link unfurls

export const unfurlParamsSchema = z.object({ messageId: uuidSchema, unfurlId: uuidSchema });
export type UnfurlParams = z.infer<typeof unfurlParamsSchema>;

/** POST /unfurls/preview — the composer's preview, through the same SSRF guard as the worker. */
export const unfurlPreviewRequestSchema = z.strictObject({ url: httpUrl });
export type UnfurlPreviewRequest = z.infer<typeof unfurlPreviewRequestSchema>;

export const unfurlPreviewResponseSchema = z.object({
  url: z.string(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  siteName: z.string().nullable(),
  /** A re-hosted image (`link_preview_image`), never a third-party URL. */
  imageFileId: uuidSchema.nullable(),
});
export type UnfurlPreviewResponse = z.infer<typeof unfurlPreviewResponseSchema>;

// ================================================================ user groups

export const userGroupIdParamsSchema = z.object({ groupId: uuidSchema });
export type UserGroupIdParams = z.infer<typeof userGroupIdParamsSchema>;

const groupHandle = z
  .string()
  .trim()
  .toLowerCase()
  .regex(USER_GROUP_HANDLE_PATTERN, 'Lowercase letters, digits, ".", "_" and "-"');

export const userGroupDtoSchema = z.object({
  id: uuidSchema,
  handle: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
  memberCount: z.number().int().nonnegative(),
  /** Guests see only people who share a conversation with them. */
  memberUserIds: z.array(uuidSchema),
  defaultConversationIds: z.array(uuidSchema),
  createdBy: uuidSchema,
  disabledAt: isoDateTimeSchema.nullable(),
  version: z.number().int().nonnegative(),
});
export type UserGroupDto = z.infer<typeof userGroupDtoSchema>;

export const listUserGroupsQuerySchema = z.object({
  includeDisabled: z
    .enum(['true', 'false', '1', '0'])
    .transform((v) => v === 'true' || v === '1')
    .default(false),
});
export type ListUserGroupsQuery = z.infer<typeof listUserGroupsQuerySchema>;

/** GET /user-groups — `version` changes whenever any group changes (bootstrap `userGroupsVersion`). */
export const userGroupListResponseSchema = z.object({
  items: z.array(userGroupDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
  version: z.number().int().nonnegative(),
});
export type UserGroupListResponse = z.infer<typeof userGroupListResponseSchema>;

/** POST /user-groups — `messaging.userGroupsManagedBy` decides who (`canManageUserGroups`). */
export const createUserGroupRequestSchema = z.strictObject({
  handle: groupHandle,
  name: singleLineText(1, 80),
  description: multiLineText(0, 250).nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  memberUserIds: z.array(uuidSchema).max(2_000),
  defaultConversationIds: z.array(uuidSchema).max(50).optional(),
});
export type CreateUserGroupRequest = z.infer<typeof createUserGroupRequestSchema>;

export const updateUserGroupRequestSchema = z.strictObject({
  handle: groupHandle.optional(),
  name: singleLineText(1, 80).optional(),
  description: multiLineText(0, 250).nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  defaultConversationIds: z.array(uuidSchema).max(50).optional(),
});
export type UpdateUserGroupRequest = z.infer<typeof updateUserGroupRequestSchema>;

/** PUT /user-groups/:groupId/members — replaces the member list. */
export const setUserGroupMembersRequestSchema = z.strictObject({
  userIds: z.array(uuidSchema).max(2_000),
});
export type SetUserGroupMembersRequest = z.infer<typeof setUserGroupMembersRequestSchema>;

// ================================================================ custom emoji

export const emojiNameParamsSchema = z.object({
  name: z.string().regex(CUSTOM_EMOJI_NAME_PATTERN, 'Unknown emoji name'),
});
export type EmojiNameParams = z.infer<typeof emojiNameParamsSchema>;

export const customEmojiDtoSchema = z.object({
  name: z.string(),
  /** The 128 px image (`custom_emoji` file); null for an alias. */
  fileId: uuidSchema.nullable(),
  aliasFor: z.string().nullable(),
  createdBy: uuidSchema,
  createdAt: isoDateTimeSchema,
});
export type CustomEmojiDto = z.infer<typeof customEmojiDtoSchema>;

/** GET /emoji — `version` is also the ETag (`If-None-Match` → `304`). */
export const customEmojiListResponseSchema = z.object({
  items: z.array(customEmojiDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
  version: z.number().int().nonnegative(),
});
export type CustomEmojiListResponse = z.infer<typeof customEmojiListResponseSchema>;

/**
 * POST /emoji is multipart (`name` field + `file`: PNG, GIF or WebP ≤ 1 MiB, resized to 128 px,
 * animation kept). This schema checks the `name` field.
 */
export const createEmojiFieldsSchema = z.strictObject({
  name: z.string().trim().toLowerCase().regex(CUSTOM_EMOJI_NAME_PATTERN, 'Use a-z, 0-9, _ + -'),
});
export type CreateEmojiFields = z.infer<typeof createEmojiFieldsSchema>;

/** POST /emoji/aliases — `aliasFor` is a custom or standard emoji. */
export const createEmojiAliasRequestSchema = z.strictObject({
  name: z.string().trim().toLowerCase().regex(CUSTOM_EMOJI_NAME_PATTERN, 'Use a-z, 0-9, _ + -'),
  aliasFor: z.string().trim().toLowerCase().regex(CUSTOM_EMOJI_NAME_PATTERN),
});
export type CreateEmojiAliasRequest = z.infer<typeof createEmojiAliasRequestSchema>;
